import { G } from '@mobily/ts-belt';
import { Dialog, DialogActions, DialogContent, Typography } from '@mui/material';
import { CreateInDialogButtonProps } from '@react-admin/ra-form-layout';
import { useCallback, useMemo, useState } from 'react';
import {
  // Button without permissions
  // eslint-disable-next-line @typescript-eslint/no-restricted-imports
  Button,
  HttpError,
  RaRecord,
  useNotify,
  useTranslate,
} from 'react-admin';

import { PostB2bWithdrawalBulkEndpoint } from '@Common/types/bo/b2b/withdrawals';
import { PostBoExternalWithdrawalBulkEndpoint } from '@Common/types/bo_external/withdrawals';
import { WITHDRAWAL_DECEASED_REQUIRES_THIRD_PARTY } from '@Common/types/error_messages';

import {
  CreateInDialogButton,
  DialogTitleWithX,
  StepperForm,
  StepperFormStep,
} from '@sh4res/bo-common/components';
import { ConfirmationStep } from '@sh4res/bo-common/components/B2bWithdrawalBulkCreate/steps/ConfirmationStep';
import { GeneralInfoStep } from '@sh4res/bo-common/components/B2bWithdrawalBulkCreate/steps/GeneralInfoStep';
import { ReviewStep } from '@sh4res/bo-common/components/B2bWithdrawalBulkCreate/steps/ReviewStep';
import { getDeceasedRowsToWithdraw } from '@sh4res/bo-common/components/B2bWithdrawalBulkCreate/steps/utils';
import { useResourceBasePath } from '@sh4res/bo-common/hooks';
import { getErrorMessage } from '@sh4res/bo-common/utils';

type WithdrawalBulkRequestBody =
  | PostB2bWithdrawalBulkEndpoint['Client']['RequestBody']
  | PostBoExternalWithdrawalBulkEndpoint['Client']['RequestBody'];

const createHandleTransform = (data: RaRecord): WithdrawalBulkRequestBody => ({
  b2b_company_id: data.b2b_company_id,
  instrument_id: data.instrument_id,
  rows: (data.bulk_rows ?? [])
    .filter((row: { employee_id?: number | null }) => typeof row.employee_id === 'number')
    .map((row: { employee_id: number; quantity_to_withdraw?: number }) => ({
      employee_id: row.employee_id,
      quantity:
        G.isNullable(row.quantity_to_withdraw) || !Number.isFinite(row.quantity_to_withdraw)
          ? 0
          : row.quantity_to_withdraw,
    }))
    .filter((row: { quantity: number }) => row.quantity > 0),
});

type Props = CreateInDialogButtonProps['ButtonProps'] & {
  b2bCompanyId?: number;
};

export function B2BWithdrawalBulkCreate(props: Props = {}) {
  const { b2bCompanyId, ...rest } = props;
  const notify = useNotify();
  const resourceBasePath = useResourceBasePath();
  const successDialog = useWithdrawalBulkCreateSuccessDialog();

  const onSuccess = useCallback(() => {
    notify('resources.bo_common.b2b.withdrawals.bulk_create.success.created', {
      type: 'info',
    });
    successDialog.open();
  }, [notify, successDialog]);

  const onError = useCallback(
    (error: unknown) => {
      if (
        error instanceof HttpError &&
        error.body?.message === WITHDRAWAL_DECEASED_REQUIRES_THIRD_PARTY
      ) {
        notify('resources.bo_common.b2b.withdrawals.bulk_create.validation.deceased_employees', {
          type: 'error',
        });
        return;
      }

      if (error instanceof HttpError) {
        notify('resources.bo_common.b2b.withdrawals.create.errors.failure', {
          type: 'error',
          messageArgs: { error: getErrorMessage(error) },
          multiLine: true,
        });
        return;
      }

      notify('resources.bo_common.b2b.withdrawals.bulk_create.errors.create_failed', {
        type: 'error',
      });
    },
    [notify],
  );

  const steps: StepperFormStep[] = useMemo(
    () => [
      {
        label: 'resources.bo_common.b2b.withdrawals.bulk_create.steps.1',
        content: <GeneralInfoStep />,
        validate: ({ getValues }) => {
          const bulkFile = getValues('bulk_file');
          const hasFile = !Array.isArray(bulkFile) || bulkFile.length > 0;

          if (!hasFile) {
            return {
              isValid: false,
              message:
                'resources.bo_common.b2b.withdrawals.bulk_create.validation.upload_file_required',
            };
          }

          return {
            isValid: true,
          };
        },
      },
      {
        label: 'resources.bo_common.b2b.withdrawals.bulk_create.steps.2',
        content: <ReviewStep />,
        nextDisabled: (values) =>
          values.bulk_rows_all_pages_visited !== true || values.bulk_rows_all_data_fetched !== true,
        validate: ({ getValues }) => {
          const rows = getValues('bulk_rows');
          const allPagesVisited = getValues('bulk_rows_all_pages_visited') === true;
          const allDataFetched = getValues('bulk_rows_all_data_fetched') === true;
          const hasRows = Array.isArray(rows) && rows.length > 0;

          if (!hasRows) {
            return {
              isValid: false,
              message: 'resources.bo_common.b2b.withdrawals.bulk_create.validation.no_rows',
            };
          }

          if (!allPagesVisited || !allDataFetched) {
            return {
              isValid: false,
              message:
                'resources.bo_common.b2b.withdrawals.bulk_create.validation.review_all_pages_and_load_data',
            };
          }

          if (getDeceasedRowsToWithdraw(rows).length > 0) {
            return {
              isValid: false,
              message:
                'resources.bo_common.b2b.withdrawals.bulk_create.validation.deceased_employees',
            };
          }

          return {
            isValid: true,
          };
        },
      },
      {
        label: 'resources.bo_common.b2b.withdrawals.bulk_create.steps.3',
        content: <ConfirmationStep />,
        // oxlint-disable-next-line react/no-unstable-nested-components -- render callback, not a component
        confirmation: (values, translate) => {
          if (values.bulk_payout_beneficiary_missing !== true) {
            return { shouldConfirm: false };
          }

          return {
            shouldConfirm: true,
            blocking: true,
            title: (
              <Typography variant="h6">
                {translate(
                  'resources.bo_common.b2b.withdrawals.bulk_create.beneficiary_missing_modal.title',
                )}
              </Typography>
            ),
            body: (
              <Typography variant="body1">
                {translate(
                  'resources.bo_common.b2b.withdrawals.bulk_create.beneficiary_missing_modal.body',
                )}
              </Typography>
            ),
          };
        },
      },
    ],
    [],
  );

  return (
    <>
      <CreateInDialogButton
        testId="BoExternalB2BWithdrawalBulkCreate_CreateButton"
        resource={`${resourceBasePath}withdrawals/bulk`}
        fullWidth
        maxWidth="xl"
        title={'resources.bo_common.b2b.withdrawals.bulk_create.title'}
        ButtonProps={{
          label: 'resources.bo_common.b2b.withdrawals.bulk_create.button',
          ...rest,
        }}
        transform={createHandleTransform}
        mutationOptions={{ onSuccess, onError }}>
        <StepperForm
          steps={steps}
          defaultValues={{ b2b_company_id: b2bCompanyId }}
          permissions={{
            resource: `${resourceBasePath}withdrawals/bulk`,
            action: 'create',
          }}
          testId="BoExternalB2BWithdrawalBulkCreate"
        />
      </CreateInDialogButton>
      {successDialog.Dialog}
    </>
  );
}

const useWithdrawalBulkCreateSuccessDialog = () => {
  const [isOpen, setOpen] = useState(false);
  const translate = useTranslate();

  const open = useCallback(() => {
    setOpen(true);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  return {
    open,
    Dialog: (
      <Dialog open={isOpen} onClose={close} maxWidth="sm" fullWidth>
        <>
          <DialogTitleWithX onClose={close}>
            {translate('resources.bo_common.b2b.withdrawals.bulk_create.success_modal.title')}
          </DialogTitleWithX>

          <DialogContent>
            <Typography variant="body1">
              {translate(
                'resources.bo_common.b2b.withdrawals.bulk_create.success_modal.description',
              )}
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button
              variant="contained"
              onClick={close}
              label={'resources.bo_common.b2b.withdrawals.bulk_create.success_modal.cta'}
              data-testid="BoExternalB2BWithdrawalBulkCreate_SuccessModalCloseButton"
            />
          </DialogActions>
        </>
      </Dialog>
    ),
  };
};
